/**
 * @file Job detail view — full job info, customer, notes, photos, actions, invoice
 */

import { getJobById, getJobCustomer, addJobNote, getNextStage, moveJobToStage } from '@services/jobs.js';
import { startTimer, getActiveTimer } from '@services/timer.js';
import { captureAndAttach } from '@services/photos.js';
import { createQuickInvoice, saveInvoice, createStripeCheckout } from '@services/invoice.js';
import { pop, navigate } from '@core/router.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';

const STAGE_BADGES = {
  'New': 'badge-blue', 'Contacted': 'badge-purple', 'Quoted': 'badge-gold',
  'Proposal Sent': 'badge-orange', 'Booked': 'badge-green', 'Proposal Rejected': 'badge-red',
  'Accepted': 'badge-blue', 'Scheduled': 'badge-purple', 'In Progress': 'badge-gold',
  'Completed': 'badge-green', 'Paid & Closed': 'badge-gray',
};

function render({ jobId }) {
  const job = getJobById(jobId);
  if (!job) {
    return `
      <button class="back-btn" id="detail-back">\u2190 Back</button>
      <div class="empty-state">
        <div class="empty-state-icon">\u26A0\uFE0F</div>
        <div class="empty-state-text">Job not found</div>
      </div>
    `;
  }

  const customer = getJobCustomer(job);
  const badge = STAGE_BADGES[job.stage] || 'badge-gray';
  const nextStage = getNextStage(job.stage);
  const notes = job.notes || [];
  const photos = job.photos || [];
  const value = job.value ? `$${Number(job.value).toLocaleString()}` : '';
  // Use name/customerName for display if no linked customer object
  const custName = customer
    ? (customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim())
    : (job.customerName || job.name || '');
  const custPhone = customer?.phone || job.phone || '';
  const custEmail = customer?.email || job.email || '';
  const custAddress = customer?.address || job.address || '';

  return `
    <button class="back-btn" id="detail-back">\u2190 Back</button>

    <div class="card mb-md">
      <div class="flex-between mb-sm">
        <h2 style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;">${esc(job.desc || job.name || 'Job')}</h2>
        <span class="badge ${badge}">${esc(job.stage)}</span>
      </div>
      ${value ? `<div class="text-gold" style="font-size:1.2rem;font-weight:700;">${value}</div>` : ''}
    </div>

    ${custName ? `
    <div class="detail-section">
      <div class="detail-section-header">Customer</div>
      <div class="detail-section-content">
        <div style="font-weight:600;font-size:1rem;margin-bottom:4px;">${esc(custName)}</div>
        ${custPhone ? `<a href="tel:${esc(custPhone)}" class="list-item" style="padding:8px 0;border:none;color:var(--blue);text-decoration:none;">\uD83D\uDCDE ${esc(custPhone)}</a>` : ''}
        ${custEmail ? `<div class="text-muted text-sm">\u2709 ${esc(custEmail)}</div>` : ''}
        ${custAddress ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(custAddress)}" target="_blank" class="list-item" style="padding:8px 0;border:none;color:var(--blue);text-decoration:none;">\uD83D\uDCCD ${esc(custAddress)}</a>` : ''}
      </div>
    </div>` : ''}

    <div class="quick-actions" id="job-quick-actions">
      ${custPhone ? `<button class="quick-action" data-action="call"><span class="qa-icon">\uD83D\uDCDE</span>Call</button>` : ''}
      ${custPhone ? `<button class="quick-action" data-action="text"><span class="qa-icon">\u2709\uFE0F</span>Text</button>` : ''}
      ${custAddress ? `<button class="quick-action" data-action="directions"><span class="qa-icon">\uD83D\uDCCD</span>Directions</button>` : ''}
      <button class="quick-action" data-action="timer"><span class="qa-icon">\u23F1</span>Timer</button>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">
        Notes (${Array.isArray(notes) ? notes.length : (typeof notes === 'string' && notes ? 1 : 0)})
        <button class="btn btn-sm btn-outline" id="add-note-btn">+ Add</button>
      </div>
      <div class="detail-section-content" id="notes-list">
        ${renderNotes(notes)}
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">
        Photos (${photos.length})
        <button class="btn btn-sm btn-outline" id="add-photo-btn">+ Photo</button>
      </div>
      <div class="detail-section-content">
        ${photos.length > 0
          ? `<div class="photo-grid">${photos.map(p => `<img src="${esc(p.url || p)}" alt="Job photo" loading="lazy" />`).join('')}</div>`
          : '<div class="text-muted text-sm">No photos yet</div>'}
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">Actions</div>
      <div class="detail-section-content flex-col gap-sm">
        ${nextStage ? `<button class="btn btn-gold btn-full" id="move-stage-btn">Move to ${esc(nextStage)}</button>` : ''}
        <button class="btn btn-outline btn-full" id="create-invoice-btn">\uD83D\uDCB0 Create Invoice</button>
      </div>
    </div>
  `;
}

function renderNotes(notes) {
  if (typeof notes === 'string') {
    if (!notes) return '<div class="text-muted text-sm">No notes yet</div>';
    return `<div class="note-item"><div class="note-text">${esc(notes)}</div></div>`;
  }
  if (Array.isArray(notes) && notes.length > 0) {
    return notes.map(n => {
      const text = typeof n === 'string' ? n : (n.text || '');
      const date = n.createdAt ? formatDate(n.createdAt) : '';
      return `<div class="note-item"><div class="note-text">${esc(text)}</div>${date ? `<div class="note-date">${date}</div>` : ''}</div>`;
    }).join('');
  }
  return '<div class="text-muted text-sm">No notes yet</div>';
}

function mount({ jobId }) {
  // Back button
  document.getElementById('detail-back')?.addEventListener('click', () => pop());

  // Quick actions
  const actions = document.getElementById('job-quick-actions');
  if (actions) {
    actions.addEventListener('click', (e) => {
      const btn = e.target.closest('.quick-action');
      if (!btn) return;
      const action = btn.dataset.action;
      const job = getJobById(jobId);
      const customer = job ? getJobCustomer(job) : null;
      const phone = customer?.phone || job?.phone || '';
      const address = customer?.address || job?.address || '';

      if (action === 'call' && phone) {
        window.open(`tel:${phone}`, '_self');
      } else if (action === 'text' && phone) {
        window.open(`sms:${phone}`, '_self');
      } else if (action === 'directions' && address) {
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
      } else if (action === 'timer') {
        const active = getActiveTimer();
        if (active) {
          toast('Timer already running. Stop it first.', 'warning');
          navigate('timer');
        } else {
          startTimer(job?.customerId, customer?.name || job?.customerName || '', jobId, job?.desc || '');
          toast('Timer started', 'success');
          navigate('timer');
        }
      }
    });
  }

  // Add note
  document.getElementById('add-note-btn')?.addEventListener('click', () => {
    showModal('Add Note', `
      <div class="form-group">
        <textarea id="new-note-text" rows="3" placeholder="Enter note..."></textarea>
      </div>
    `, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Save', type: 'gold', onClick: () => {
        const text = document.getElementById('new-note-text')?.value?.trim();
        if (!text) return;
        addJobNote(jobId, text);
        closeModal();
        toast('Note added', 'success');
        rerender(jobId);
      }},
    ]);
  });

  // Add photo
  document.getElementById('add-photo-btn')?.addEventListener('click', async () => {
    toast('Opening camera...', 'info');
    const url = await captureAndAttach(jobId);
    if (url) {
      toast('Photo attached', 'success');
      rerender(jobId);
    }
  });

  // Move stage
  document.getElementById('move-stage-btn')?.addEventListener('click', () => {
    const job = getJobById(jobId);
    const nextStage = job ? getNextStage(job.stage) : null;
    if (!nextStage) return;
    showModal('Move Job', `<p>Move this job to <strong>${esc(nextStage)}</strong>?</p>`, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Move', type: 'gold', onClick: () => {
        moveJobToStage(jobId, nextStage);
        closeModal();
        toast(`Moved to ${nextStage}`, 'success');
        rerender(jobId);
      }},
    ]);
  });

  // Create invoice - full modal with line items
  document.getElementById('create-invoice-btn')?.addEventListener('click', () => {
    const job = getJobById(jobId);
    if (!job) return;
    const customer = getJobCustomer(job);
    const custName = customer
      ? (customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim())
      : (job.customerName || job.name || 'Customer');

    // Pre-populate line items
    const defaultDesc = job.desc || job.name || 'Handyman Service';
    const defaultAmount = job.value || job.amount || 0;

    showModal('Create Invoice', `
      <div class="form-group">
        <label>Customer</label>
        <input type="text" id="inv-customer" value="${esc(custName)}" readonly style="background:var(--navy-lighter);opacity:0.8;" />
      </div>

      <div class="detail-section-header" style="padding:8px 0;margin-top:8px;">Line Items</div>
      <div id="inv-line-items">
        <div class="inv-line-item" style="display:flex;gap:6px;margin-bottom:8px;">
          <input type="text" class="inv-item-desc" placeholder="Description" value="${esc(defaultDesc)}" style="flex:2;" />
          <input type="number" class="inv-item-qty" placeholder="Qty" value="1" style="flex:0.5;min-width:50px;" />
          <input type="number" class="inv-item-rate" placeholder="Rate" value="${defaultAmount}" style="flex:1;min-width:70px;" />
        </div>
      </div>
      <button class="btn btn-sm btn-outline" id="inv-add-line" style="margin-bottom:12px;">+ Add Line</button>

      <div style="border-top:1px solid rgba(255,255,255,0.1);padding-top:10px;">
        <div class="flex-between mb-sm">
          <span>Subtotal</span>
          <span id="inv-subtotal">$${Number(defaultAmount).toLocaleString()}</span>
        </div>
        <div class="flex-between mb-sm">
          <span>Tax (7%)</span>
          <span id="inv-tax">$${(defaultAmount * 0.07).toFixed(2)}</span>
        </div>
        <div class="flex-between" style="font-weight:700;font-size:1.1rem;color:var(--gold);">
          <span>Total</span>
          <span id="inv-total">$${(defaultAmount * 1.07).toFixed(2)}</span>
        </div>
      </div>
    `, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Save Invoice', type: 'gold', onClick: () => {
        const lineItems = collectLineItems();
        const subtotal = lineItems.reduce((s, i) => s + i.total, 0);
        const tax = Math.round(subtotal * 0.07 * 100) / 100;
        const total = subtotal + tax;

        const invoice = {
          id: Date.now().toString(36),
          jobId: job.id,
          customerId: job.customerId,
          customerName: custName,
          customerEmail: customer?.email || job.email || '',
          lineItems,
          subtotal,
          tax,
          total,
          status: 'draft',
          createdAt: new Date().toISOString(),
        };

        saveInvoice(invoice);
        closeModal();
        toast(`Invoice saved: $${total.toFixed(2)}`, 'success');
      }},
    ]);

    // Wire up add line item button and live calculation
    setTimeout(() => {
      document.getElementById('inv-add-line')?.addEventListener('click', () => {
        const container = document.getElementById('inv-line-items');
        if (!container) return;
        const row = document.createElement('div');
        row.className = 'inv-line-item';
        row.style.cssText = 'display:flex;gap:6px;margin-bottom:8px;';
        row.innerHTML = `
          <input type="text" class="inv-item-desc" placeholder="Description" style="flex:2;" />
          <input type="number" class="inv-item-qty" placeholder="Qty" value="1" style="flex:0.5;min-width:50px;" />
          <input type="number" class="inv-item-rate" placeholder="Rate" value="0" style="flex:1;min-width:70px;" />
        `;
        container.appendChild(row);
        recalcInvoice();
      });

      // Live recalc on any input change
      document.getElementById('inv-line-items')?.addEventListener('input', recalcInvoice);
    }, 100);
  });
}

function collectLineItems() {
  const items = [];
  document.querySelectorAll('.inv-line-item').forEach(row => {
    const desc = row.querySelector('.inv-item-desc')?.value?.trim() || 'Service';
    const qty = parseFloat(row.querySelector('.inv-item-qty')?.value) || 1;
    const rate = parseFloat(row.querySelector('.inv-item-rate')?.value) || 0;
    if (rate > 0 || desc) {
      items.push({ description: desc, quantity: qty, unitPrice: rate, total: qty * rate });
    }
  });
  return items;
}

function recalcInvoice() {
  const items = collectLineItems();
  const subtotal = items.reduce((s, i) => s + i.total, 0);
  const tax = Math.round(subtotal * 0.07 * 100) / 100;
  const total = subtotal + tax;
  const subEl = document.getElementById('inv-subtotal');
  const taxEl = document.getElementById('inv-tax');
  const totalEl = document.getElementById('inv-total');
  if (subEl) subEl.textContent = `$${subtotal.toLocaleString()}`;
  if (taxEl) taxEl.textContent = `$${tax.toFixed(2)}`;
  if (totalEl) totalEl.textContent = `$${total.toFixed(2)}`;
}

function rerender(jobId) {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render({ jobId });
    mount({ jobId });
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) { return ''; }
}

export default { render, mount, unmount };
